<template>
  <nav class="bg-gray-800">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-between h-16">
        <div class="flex items-center">
          <div
            class="py-1"
            style="max-height: 50px; max-width: 100px;"
          >
            <Logo :light="true" />
          </div>
          <div class="hidden md:block">
            <div class="ml-10 flex items-baseline space-x-4">
              <NuxtLink
                class="px-3 py-2 rounded-md text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700"
                to="/"
                exact-active-class="bg-bray-900"
              >
                Dashboard
              </NuxtLink>

              <NuxtLink
                v-if="isValidator"
                class="px-3 py-2 rounded-md text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700"
                to="/customers"
              >
                Customers
              </NuxtLink>

              <NuxtLink
                v-if="isValidator"
                class="px-3 py-2 rounded-md text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700"
                to="/sales"
              >
                Sales
              </NuxtLink>

              <NuxtLink
                class="px-3 py-2 rounded-md text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700"
                to="/calls"
              >
                Calls
              </NuxtLink>

              <NuxtLink
                v-if="isAdmin"
                class="px-3 py-2 rounded-md text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700"
                to="/leads"
              >
                Leads
              </NuxtLink>

              <NuxtLink
                v-if="isAdmin"
                class="px-3 py-2 rounded-md text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700"
                to="/reports"
              >
                Reports
              </NuxtLink>

              <NuxtLink
                v-if="isAdmin"
                class="px-3 py-2 rounded-md text-sm font-medium text-gray-300 hover:text-white hover:bg-gray-700"
                to="/configuration"
              >
                Configuration
              </NuxtLink>
            </div>
          </div>
        </div>
        <div class="hidden md:block">
          <div class="ml-4 flex items-center md:ml-6">
            <div class="ml-3">
              <div class="text-base font-medium leading-none text-white">
                {{ getName }}
              </div>
              <div class="text-sm font-medium leading-none text-gray-400">
                {{ getEmail }}
              </div>
            </div>
            <!-- Profile dropdown -->
            <div class="ml-3 relative">
              <div>
                <button
                  id="user-menu"
                  class="bg-gray-800 p-1 rounded-full text-gray-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white"
                  @click="toggleUserProfileMenu"
                >
                  <span class="sr-only">View user menu</span>
                  <!-- Heroicon name: user -->
                  <svg class="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </button>
              </div>
              <!--
                Profile dropdown panel, show/hide based on dropdown state.

                Entering: "transition ease-out duration-100"
                  From: "transform opacity-0 scale-95"
                  To: "transform opacity-100 scale-100"
                Leaving: "transition ease-in duration-75"
                  From: "transform opacity-100 scale-100"
                  To: "transform opacity-0 scale-95"
              -->
              <transition
                enter-active-class="transition ease-out duration-100"
                enter-class="transform opacity-0 scale-95"
                enter-to-class="transform opacity-100 scale-100"
                leave-active-class="transition ease-in duration-75"
                leave-class="transform opacity-100 scale-100"
                leave-to-class="transform opacity-0 scale-95"
              >
                <div
                  v-show="userProfileMenuOpened"
                  class="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5"
                  role="menu"
                  aria-orientation="vertical"
                  aria-labelledby="user-menu"
                >
                  <NuxtLink
                    class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    to="/user/holidays"
                  >
                    Your Holidays
                  </NuxtLink>

                  <hr>
                  <a
                    class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                    @click="handleLogout"
                  >
                    Log out
                  </a>
                </div>
              </transition>
            </div>
          </div>
        </div>
        <div class="-mr-2 flex md:hidden">
          <!-- Mobile menu button -->
          <button
            class="bg-gray-800 inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-800 focus:ring-white"
            @click="toggleMobileMenu"
          >
            <span class="sr-only">Open main menu</span>
            <!--
              Heroicon name: menu

              Menu open: "hidden", Menu closed: "block"
            -->
            <svg
              :class="{'block': !mobileMenuOpened, 'hidden': mobileMenuOpened}"
              class="h-6 w-6"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
            <!--
              Heroicon name: x

              Menu open: "block", Menu closed: "hidden"
            -->
            <svg
              :class="{'block': mobileMenuOpened, 'hidden': !mobileMenuOpened}"
              class="h-6 w-6"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!--
      Mobile menu, toggle classes based on menu state.

      Open: "block", closed: "hidden"
    -->
    <div class="md:hidden" :class="{'hidden': !mobileMenuOpened}">
      <div class="px-2 pt-2 pb-3 space-y-1 sm:px-3">
        <NuxtLink
          class="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-gray-700"
          to="/"
        >
          Dashboard
        </NuxtLink>

        <NuxtLink
          v-if="isAdmin"
          class="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-gray-700"
          to="/customers"
        >
          Customers
        </NuxtLink>

        <NuxtLink
          v-if="isValidator"
          class="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-gray-700"
          to="/sales"
        >
          Sales
        </NuxtLink>

        <NuxtLink
          v-if="isAdmin"
          class="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-gray-700"
          to="/reports"
        >
          Reports
        </NuxtLink>

        <NuxtLink
          v-if="isAdmin"
          class="block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:text-white hover:bg-gray-700"
          to="/configuration"
        >
          Configuration
        </NuxtLink>
      </div>
      <div class="pt-4 pb-3 border-t border-gray-700">
        <div class="flex items-center px-5">
          <div>
            <div class="text-base font-medium leading-none text-white">
              {{ getName }}
            </div>
            <div class="text-sm font-medium leading-none text-gray-400">
              {{ getEmail }}
            </div>
          </div>
        </div>
        <div class="mt-3 px-2 space-y-1">
          <NuxtLink
            class="block px-3 py-2 rounded-md text-base font-medium text-gray-400 hover:text-white hover:bg-gray-700"
            to="/user/holidays"
          >
            Your Holidays
          </NuxtLink>

          <a
            class="block px-3 py-2 rounded-md text-base font-medium text-gray-400 hover:text-white hover:bg-gray-700 cursor-pointer"
            @click="handleLogout"
          >
            Log out
          </a>
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
import { mapState } from 'pinia'

export default {
  setup () {
    const { user, isAdmin, isValidator } = useAuth()
    return {
      user,
      isAdmin,
      isValidator
    }
  },
  data () {
    return {
      userProfileMenuOpened: false,
      mobileMenuOpened: false
    }
  },
  computed: {
    ...mapState(useProfileStore, [
      'getName',
      'getEmail'
    ])
  },
  watch: {
    '$route' () {
      this.mobileMenuOpened = false
      this.userProfileMenuOpened = false
    }
  },
  methods: {
    toggleUserProfileMenu () {
      this.userProfileMenuOpened = !this.userProfileMenuOpened
    },
    toggleMobileMenu () {
      this.mobileMenuOpened = !this.mobileMenuOpened
    },
    async handleLogout () {
      const { logout } = useAuth()
      await logout()
    }
  }
}
</script>

<style>
  nav .router-link-active {
    @apply text-white bg-gray-900;
  }
</style>
